import { Column, TablePaginated, TablePaginatedOptions } from '@acceso/design-system';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import {
  ChannelMediaTypeEnum,
  ChannelScopeEnum,
  EconomicalValueChannelSourceType,
  EconomicalValueProviderEnum,
} from '@/features/broadcasters/types';
import { useLocationCodeToLabel } from '@/features/locations/hooks/useGetLocationLabelFromCode';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { createTableFilterOptions, getParamsFromTableFilters } from '@/utils/table-filters';

import { useGetEconomicalValueChannelSourcesList } from '../../api/get-economical-value-channel-sources-list';
import { useEconomicalValueChannelSourcesFilters } from '../../stores/economical-value-channel-sources-filters';

interface EconomicalValueChannelSourcesTableProps {
  onDelete?: (item: EconomicalValueChannelSourceType) => void;
}

export function EconomicalValueChannelSourcesTable({
  onDelete,
}: EconomicalValueChannelSourcesTableProps) {
  const { t } = useTranslation();
  const locationCodeToLabel = useLocationCodeToLabel();
  const scopeOptions = useChannelScopeOptions();
  const mediaTypeOptions = useChannelMediaTypeOptions();

  const {
    filters,
    paginationValues,
    setPage,
    setRows,
    setFilters,
    clearFilters,
    setSearchQuery,
    setSortBy,
  } = useEconomicalValueChannelSourcesFilters();

  const { countryOptions, regionOptions } = useLocationOptions({
    countryCode: filters.countryIn ?? null,
  });

  const { data: { data: items = [], total: totalItems = 0 } = {}, isFetching } =
    useGetEconomicalValueChannelSourcesList({
      params: getParamsFromTableFilters({ filters, paginationValues }),
      config: {
        placeholderData: keepPreviousData,
      },
    });

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    searchBarPlaceholder: t('Buscar por nombre'),
    onCleanAllFilters: clearFilters,
    filters: [
      {
        name: 'mediaTypeIn',
        placeholder: t('Tipo de medio'),
        options: createTableFilterOptions({
          options: mediaTypeOptions,
          allOptionLabel: t('Cualquier tipo'),
        }),
        onChange: value => {
          setFilters({
            mediaTypeIn: ((value as string) || undefined) as ChannelMediaTypeEnum | undefined,
          });
        },
      },
      {
        name: 'scopeIn',
        placeholder: t('Scope'),
        options: createTableFilterOptions({
          options: scopeOptions.filter(opt => opt.value !== null),
          allOptionLabel: 'Cualquier scope',
        }),
        onChange: value => {
          setFilters({ scopeIn: ((value as string) || undefined) as ChannelScopeEnum | undefined });
        },
      },
      {
        name: 'providerIn',
        placeholder: t('Proveedor'),
        options: createTableFilterOptions({
          options: [{ label: 'Mecos', value: EconomicalValueProviderEnum.MECOS }],
          allOptionLabel: 'Cualquier proveedor',
        }),
        onChange: value => {
          setFilters({
            providerIn: ((value as string) || undefined) as EconomicalValueProviderEnum | undefined,
          });
        },
      },
      {
        name: 'countryIn',
        placeholder: t('País'),
        options: createTableFilterOptions({
          options: countryOptions,
          allOptionLabel: t('Cualquier país'),
        }),
        onChange: value => {
          setFilters({ countryIn: (value as string) || undefined, regionIn: undefined });
        },
      },
      {
        name: 'regionIn',
        placeholder: t('Región'),
        options: createTableFilterOptions({
          options: regionOptions,
          allOptionLabel: t('Cualquier región'),
        }),
        onChange: value => {
          setFilters({ regionIn: (value as string) || undefined });
        },
      },
    ],
  };

  const columns: Column<EconomicalValueChannelSourceType>[] = useMemo(
    (): Column<EconomicalValueChannelSourceType>[] => [
      {
        id: 'name',
        title: t('Nombre'),
        sortable: true,
        render: item => <TableRow content={item.name} />,
      },
      {
        id: 'media_type',
        title: t('Tipo de medio'),
        sortable: true,
        render: item => (
          <TableRow
            content={
              mediaTypeOptions.find(opt => opt.value === item.media_type)?.label ?? item.media_type
            }
          />
        ),
      },
      {
        id: 'provider',
        title: t('Proveedor'),
        sortable: true,
        render: item => <TableRow content={item.provider} />,
      },
      {
        id: 'scope',
        title: t('Scope'),
        sortable: true,
        render: item => (
          <TableRow
            content={scopeOptions.find(opt => opt.value === item.scope)?.label ?? item.scope}
          />
        ),
      },
      {
        id: 'country_code',
        title: t('País'),
        sortable: true,
        render: item => (
          <TableRow content={locationCodeToLabel[item.country_code] ?? item.country_code} />
        ),
      },
      {
        id: 'region_code',
        title: t('Región'),
        sortable: true,
        render: item => (
          <TableRow
            content={
              item.region_code ? (locationCodeToLabel[item.region_code] ?? item.region_code) : '—'
            }
          />
        ),
      },
      {
        id: 'actions',
        title: t('Acciones'),
        className: 'items-end',
        render: item => (
          <RenderActionButtons item={item} onDelete={onDelete ? () => onDelete(item) : undefined} />
        ),
      },
    ],
    [
      t,
      onDelete,
      locationCodeToLabel,
      scopeOptions,
      mediaTypeOptions,
      countryOptions,
      regionOptions,
    ]
  );

  return (
    <TablePaginated
      data={items}
      totalItems={totalItems}
      columns={columns}
      isFetching={isFetching}
      options={options}
      showFilter
      ignoreEmpty
    />
  );
}
